import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import { useSignedIn } from '../session';
import { Corridor, CorridorPlace } from '../types';
import CorridorMap from './CorridorMap';
import CorridorSetup from './CorridorSetup';
import Dialog from './Dialog';


/**
 * Potvrda mesta kroz koja pravci prolaze.
 *
 * Ovo nije red kao onaj za adrese. Tamo se svaka adresa gleda za sebe; ovde se
 * gleda LINIJA, jer se pogrešna tačka prepoznaje po tome što lomi put. Otud
 * mapa u sredini ekrana i potvrda cele linije jednim potezom — kad operater
 * vidi da put ide kako treba, četrdeset pet klikova ne dodaje nijedan podatak.
 *
 * Predlog ture liniju i pojas crta samo kroz potvrđena mesta (od 19.08.2026);
 * nepotvrđena preskače i prijavljuje. Vidi TourProposalService.BuildAsync.
 */
export default function CorridorQueue() {
  const signedIn = useSignedIn();
  const [corridors, setCorridors] = useState<Corridor[]>([]);
  const [openId, setOpenId] = useState<number | null>(null);
  const [selectedPlaceId, setSelectedPlaceId] = useState<number | null>(null);

  /** Tačke koje je operater pomerio, a još nije potvrdio. */
  const [moved, setMoved] = useState<Record<number, { latitude: number; longitude: number }>>({});

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  /** Novo mesto koje se ubacuje u pravac, i iza kog mesta ide. */
  const [newPlace, setNewPlace] = useState('');
  const [afterId, setAfterId] = useState<number | null>(null);

  /** Mesto koje se izbacuje, i pravac čija se cela linija potvrđuje. */
  const [removingPlace, setRemovingPlace] = useState<CorridorPlace | null>(null);
  const [confirmingLine, setConfirmingLine] = useState<Corridor | null>(null);

  const load = useCallback(async () => {
    try {
      const all = await api.corridors();
      setCorridors(all);
      setError(null);

      // Otvara se prvi pravac koji nešto čeka — posao je tamo, ne na prvom u nizu.
      setOpenId((current) => current ?? (all.find((c) => !c.isUsable) ?? all[0])?.id ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const open = corridors.find((c) => c.id === openId) ?? null;

  /** Mesta sa pomerenim tačkama, da mapa odmah pokaže gde bi linija išla. */
  const placesOnMap: CorridorPlace[] = (open?.places ?? []).map((place) => {
    const change = moved[place.id];
    return change ? { ...place, ...change } : place;
  });

  /**
   * Ubacuje mesto u put pravca.
   *
   * Ulazi NEPOTVRĐENO i bez tačke: koordinata se traži dugmetom „predloži", pa
   * se pogleda na mapi. Isti red kao i za sva ostala mesta — predlog geokodera
   * nije potvrda, a jedno pogrešno mesto lomi celu liniju.
   */
  const addPlace = async () => {
    const naziv = newPlace.trim();
    if (!open || naziv === '') return;

    setBusy(true);
    try {
      const after = afterId === null
        ? null
        : open.places.find((p) => p.id === afterId)?.sequence ?? null;

      await api.addCorridorPlace(open.id, naziv, after);
      setNewPlace('');
      setAfterId(null);
      setDone(`${naziv} je dodato — nađi mu tačku i potvrdi je na mapi.`);
      await load();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const removePlace = async () => {
    if (!removingPlace) return;

    setBusy(true);
    try {
      await api.removeCorridorPlace(removingPlace.id);
      setRemovingPlace(null);
      await load();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Traži tačku mestu koje je nema.
   *
   * Novo mesto na pravcu ulazi bez koordinate, pa se nema šta ni prevući ni
   * potvrditi. Geokoder ponudi tačku, ona se postavi kao POMERENA — dakle vidi
   * se na mapi, ali još nije potvrđena — i operater je pogleda pa potvrdi.
   */
  const findPoint = async (place: CorridorPlace) => {
    setBusy(true);
    try {
      const result = await api.suggestPlace(place.id);
      const first = result.candidates[0];

      if (!first) {
        setError(
          `Za „${place.placeName}" nema predloga unutar Vojvodine. `
          + 'Izaberi mesto u spisku pa klikni na mapu gde jeste.');
        return;
      }

      setSelectedPlaceId(place.id);
      setMoved((current) => ({
        ...current,
        [place.id]: { latitude: first.latitude, longitude: first.longitude }
      }));

      setDone(`Predlog za ${place.placeName}: ${first.displayName}. Pogledaj pa potvrdi.`);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmPlace = async (place: CorridorPlace) => {
    const name = signedIn.displayName;

    const point = moved[place.id] ?? { latitude: place.latitude, longitude: place.longitude };
    if (point.latitude === null || point.longitude === null) {
      setError(`${place.placeName} nema tačku — prevuci čiodu na mapi pre potvrde.`);
      return;
    }

    setBusy(true);
    try {
      await api.confirmCorridorPlace(place.id, point.latitude, point.longitude, name);
      setMoved(({ [place.id]: _removed, ...rest }) => rest);
      setDone(`${place.placeName} potvrđeno.`);
      setError(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmWholeLine = async () => {
    const corridor = confirmingLine;
    if (!corridor) return;

    const name = signedIn.displayName;

    setBusy(true);
    try {
      const result = await api.confirmCorridor(corridor.id, name);
      setDone(
        result.withoutPoint.length > 0
          ? `Potvrđeno ${result.confirmed}; bez tačke ostalo: ${result.withoutPoint.join(', ')}.`
          : `Potvrđeno ${result.confirmed} mesta — pravac se od sada koristi.`);
      setError(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="corridor">
      {/* Podešavanje ide iznad potvrde: prvo se zna KOJI pravac postoji i kome
          nosi robu, pa tek onda kuda tačno vodi put. */}
      <CorridorSetup corridors={corridors} onChanged={() => void load()} />

      {error && <div className="health health--bad">{error}</div>}
      {done && <div className="health health--ok">{done}</div>}

      <section className="panel">
        <h2>Pravci</h2>
        <p className="note">
          Koordinate su predlog geokodera, a znaju da promaše grubo: za „Stapar" je
          jednom vraćeno selo kod Valjeva. To se u spisku ne vidi, na liniji da.
          Predlog ture liniju i pojas crta SAMO kroz potvrđena mesta; nepotvrđena
          preskače i prijavljuje.
        </p>

        <ul className="corridor__list">
          {corridors.map((corridor) => (
            <li key={corridor.id}>
              <button
                type="button"
                className={corridor.id === openId ? 'corridor__pick corridor__pick--open' : 'corridor__pick'}
                onClick={() => { setOpenId(corridor.id); setSelectedPlaceId(null); }}
              >
                {corridor.name}
                <span className="muted"> · {corridor.days}</span>
                <span className={corridor.isUsable ? 'flag flag--ok' : 'flag flag--warn'}>
                  {corridor.confirmed}/{corridor.total} potvrđeno
                </span>
              </button>
            </li>
          ))}
        </ul>
      </section>

      {open && (
        <section className="panel">
          <h2>{open.name}</h2>

          <div className="corridor__body">
            <CorridorMap
              onPlace={(latitude, longitude) => {
                if (selectedPlaceId === null) return;
                setMoved((current) => ({ ...current, [selectedPlaceId]: { latitude, longitude } }));
              }}
              places={placesOnMap}
              selectedId={selectedPlaceId}
              onSelect={setSelectedPlaceId}
              onMove={(id, latitude, longitude) =>
                setMoved((current) => ({ ...current, [id]: { latitude, longitude } }))}
            />

            <p className="note">
              Mesto bez tačke: pritisni „Nađi tačku" pa pogledaj gde je pala, ili
              izaberi mesto u spisku i klikni na mapu tamo gde stvarno jeste.
            </p>

            <ol className="corridor__places">
              {open.places.map((place) => {
                const change = moved[place.id];

                return (
                  <li
                    key={place.id}
                    className={place.id === selectedPlaceId ? 'corridor__place corridor__place--open' : 'corridor__place'}
                    onClick={() => setSelectedPlaceId(place.id)}
                  >
                    <span className="corridor__name">
                      {place.sequence}. {place.placeName}
                      {place.latitude === null && !change && (
                        <span className="muted"> · bez tačke</span>
                      )}
                    </span>

                    {place.latitude === null && !change && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={(e) => { e.stopPropagation(); void findPoint(place); }}
                      >
                        Nađi tačku
                      </button>
                    )}

                    {place.isVerified && !change ? (
                      <span className="flag flag--ok" title={place.verifiedBy ?? ''}>potvrđeno</span>
                    ) : (place.latitude === null && !change) ? null : (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={(e) => { e.stopPropagation(); void confirmPlace(place); }}
                      >
                        {change ? 'Potvrdi pomerenu' : 'Potvrdi'}
                      </button>
                    )}

                    <button
                      type="button"
                      className="link link--danger corridor__remove"
                      disabled={busy}
                      onClick={(e) => { e.stopPropagation(); setRemovingPlace(place); }}
                    >
                      izbaci
                    </button>
                  </li>
                );
              })}
            </ol>

            {/* Put se menja i posle postavljanja: otvori se nova prodavnica, ili
                se ispostavi da vozilo ide kroz mesto koje niko nije upisao. */}
            <div className="corridor__add">
              <input
                value={newPlace}
                placeholder="Dodaj mesto na put — npr. Ratkovo"
                onChange={(e) => setNewPlace(e.target.value)}
              />

              <select value={afterId ?? ''} onChange={(e) => setAfterId(e.target.value === '' ? null : Number(e.target.value))}>
                <option value="">na kraj</option>
                {open.places.map((place) => (
                  <option key={place.id} value={place.id}>iza {place.sequence}. {place.placeName}</option>
                ))}
              </select>

              <button
                type="button"
                className="primary"
                disabled={busy || newPlace.trim() === ''}
                onClick={() => void addPlace()}
              >
                Dodaj
              </button>
            </div>
          </div>

          {open.confirmed < open.total && (
            <button
              type="button"
              className="primary"
              disabled={busy}
              onClick={() => setConfirmingLine(open)}
            >
              Linija je tačna — potvrdi svih {open.total - open.confirmed}
            </button>
          )}
        </section>
      )}

      {removingPlace && (
        <Dialog
          title={`Izbaciti ${removingPlace.placeName} iz pravca?`}
          description={
            <>Put se time menja, pa se menja i pojas u kom se nude usputne adrese.
            Mesto se može dodati ponovo, ali će morati da se potvrdi na mapi.</>
          }
          confirmLabel="Izbaci mesto"
          danger
          busy={busy}
          onCancel={() => setRemovingPlace(null)}
          onConfirm={() => void removePlace()}
        />
      )}

      {confirmingLine && (
        <Dialog
          title={`Linija „${confirmingLine.name}“ je tačna?`}
          description={
            <>Potvrđuje se {confirmingLine.total - confirmingLine.confirmed} mesta
            odjednom, sa tačkama kakve su sad na mapi. Pojedinačno pomerene tačke
            prvo potvrdi zasebno.</>
          }
          confirmLabel="Potvrdi celu liniju"
          busy={busy}
          onCancel={() => setConfirmingLine(null)}
          onConfirm={() => void confirmWholeLine()}
        />
      )}
    </div>
  );
}
